import { type ReactNode, useState } from 'react';
import { ExportAppearanceControls } from './export-appearance-controls';
import type { ExportOptionsState } from './export-customization';
import { ExportDressingControls } from './export-dressing-controls';
import { type ExportCategoryId, isCategoryVisible } from './export-format-controls';
import { ExportOptionCategory } from './export-option-category';
import { ExportPageControls } from './export-page-controls';
import type { ExportFormat } from './export-selection';
import { ExportTableControls } from './export-table-controls';

interface ExportOptionCategoriesProps {
  format: ExportFormat;
  options: ExportOptionsState;
  onChange: (next: ExportOptionsState) => void;
  /** Decision L6, computed by the page from the live tiling. */
  repeatHeadersEffective: boolean;
  /** Decision L5 advisory under « Ajustement », computed by the page. */
  legibilityNotice: string | null;
  /**
   * The selection's « Noms des leviers » field. It is selection state, not
   * option state, so the page owns and renders it; it sits under Tableau for
   * every format.
   */
  levierNamesControl?: ReactNode;
  /** The Contenu category's body — the page's column/dimension selection. */
  children?: ReactNode;
}

/** Spec: Page and Tableau start expanded — the settings people reach for first. */
const INITIAL_OPEN: Record<ExportCategoryId, boolean> = {
  page: true,
  tableau: true,
  contenu: false,
  habillage: false,
  apparence: false,
};

/**
 * The export panel's option body — export-rework chantier 2 (plan Task 3):
 * five collapsible categories bound to the chantier-1 option blocks through a
 * single `onChange`. Dumb by design: every derived value (tiling hints,
 * legibility) arrives computed, so the page keeps one derivation.
 *
 * Masking per format lives in one table (`export-format-controls.ts`, issue
 * #12): this component asks it which categories to show, and each category's
 * controls ask it which of their fields to show.
 */
export function ExportOptionCategories({
  format,
  options,
  onChange,
  repeatHeadersEffective,
  legibilityNotice,
  levierNamesControl,
  children,
}: ExportOptionCategoriesProps) {
  // Excel shows no Page and a two-control Tableau, so on a mount there the
  // column/dimension selection is what the panel would otherwise leave hidden.
  const [open, setOpen] = useState<Record<ExportCategoryId, boolean>>(() => ({
    ...INITIAL_OPEN,
    contenu: format === 'excel',
  }));
  const toggle = (id: ExportCategoryId) => () =>
    setOpen((current) => ({ ...current, [id]: !current[id] }));
  const shows = (id: ExportCategoryId) => isCategoryVisible(format, id);

  return (
    <div>
      {shows('page') && (
        <ExportOptionCategory title="Page" open={open.page} onToggle={toggle('page')}>
          <ExportPageControls
            page={options.page}
            onChange={(page) => onChange({ ...options, page })}
            legibilityNotice={legibilityNotice}
          />
        </ExportOptionCategory>
      )}
      {shows('tableau') && (
        <ExportOptionCategory title="Tableau" open={open.tableau} onToggle={toggle('tableau')}>
          <ExportTableControls
            format={format}
            table={options.table}
            onChange={(table) => onChange({ ...options, table })}
            repeatHeadersEffective={repeatHeadersEffective}
            levierNamesControl={levierNamesControl}
          />
        </ExportOptionCategory>
      )}
      {shows('contenu') && (
        <ExportOptionCategory title="Contenu" open={open.contenu} onToggle={toggle('contenu')}>
          {children}
        </ExportOptionCategory>
      )}
      {shows('habillage') && (
        <ExportOptionCategory
          title="Habillage"
          open={open.habillage}
          onToggle={toggle('habillage')}
        >
          <ExportDressingControls
            format={format}
            dressing={options.dressing}
            onChange={(dressing) => onChange({ ...options, dressing })}
          />
        </ExportOptionCategory>
      )}
      {shows('apparence') && (
        <ExportOptionCategory
          title="Apparence"
          open={open.apparence}
          onToggle={toggle('apparence')}
        >
          <ExportAppearanceControls
            appearance={options.appearance}
            onChange={(appearance) => onChange({ ...options, appearance })}
          />
        </ExportOptionCategory>
      )}
    </div>
  );
}
